<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MediaLibraryAPI from 'dashboard/api/mediaLibrary';
import Button from 'dashboard/components-next/button/Button.vue';
import MediaLibraryDelete from 'dashboard/components-next/media-library/MediaLibraryDelete.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  taxonomy: { type: Object, required: true },
  contained: Boolean,
});
const emit = defineEmits(['saved', 'deleted', 'close', 'open-product', 'busy']);
const { t } = useI18n();
const kind = ref('products');
const draft = ref({ name: '', category_id: null, product_class_id: null });
const requirements = ref('');
const saving = ref(false);
const error = ref('');
const records = computed(() => props.taxonomy[kind.value] || []);
const label = value =>
  ({
    tags: t('MEDIA_LIBRARY.TAGS'),
    categories: t('MEDIA_LIBRARY.TAXONOMY.categories'),
    products: t('MEDIA_LIBRARY.TAXONOMY.products'),
    product_classes: t('MEDIA_LIBRARY.TAXONOMY.product_classes'),
  })[value];
const kinds = [
  { value: 'products', icon: 'i-lucide-package' },
  { value: 'categories', icon: 'i-lucide-folder' },
  { value: 'product_classes', icon: 'i-lucide-list-checks' },
  { value: 'tags', icon: 'i-lucide-tags' },
];
const options = entries => [
  { value: '', label: t('MEDIA_LIBRARY.NONE') },
  ...entries.map(entry => ({ value: entry.id, label: entry.name })),
];
function edit(record = null) {
  draft.value = record
    ? { ...record }
    : { name: '', category_id: null, product_class_id: null };
  requirements.value = (record?.photo_requirements || []).join('\n');
  error.value = '';
}
watch(kind, () => edit());
async function save() {
  saving.value = true;
  emit('busy', true);
  error.value = '';
  try {
    const body = { id: draft.value.id, name: draft.value.name.trim() };
    if (kind.value === 'products') {
      body.category_id = draft.value.category_id || null;
      body.product_class_id = draft.value.product_class_id || null;
    }
    if (kind.value === 'product_classes') {
      body.photo_requirements = [
        ...new Set(
          requirements.value
            .split('\n')
            .map(value => value.trim())
            .filter(Boolean)
        ),
      ];
    }
    await MediaLibraryAPI.saveTaxonomy(kind.value, body);
    emit('saved');
    edit();
  } catch {
    error.value = t('MEDIA_LIBRARY.TAXONOMY.SAVE_ERROR');
  } finally {
    saving.value = false;
    emit('busy', false);
  }
}
</script>

<template>
  <section
    class="bg-n-solid-2 text-sm text-n-slate-12"
    :class="contained ? '' : 'rounded-xl border border-n-weak'"
    :aria-label="t('MEDIA_LIBRARY.ORGANIZE')"
  >
    <header
      v-if="!contained"
      class="flex items-start gap-4 border-b border-n-weak p-4"
    >
      <div class="min-w-0 flex-1">
        <h2 class="m-0 text-base font-semibold">
          {{ t('MEDIA_LIBRARY.ORGANIZE') }}
        </h2>
        <p class="mb-0 mt-1 max-w-3xl text-sm text-n-slate-11">
          {{ t('MEDIA_LIBRARY.TAXONOMY.HELP') }}
        </p>
      </div>
      <Button
        icon="i-lucide-x"
        variant="ghost"
        color="slate"
        :aria-label="t('MEDIA_LIBRARY.CLOSE')"
        :title="t('MEDIA_LIBRARY.CLOSE')"
        :disabled="saving"
        @click="emit('close')"
      />
    </header>
    <div
      role="group"
      :aria-label="t('MEDIA_LIBRARY.TAXONOMY.MANAGE')"
      class="flex flex-wrap gap-1 border-b border-n-weak px-4 py-3"
    >
      <Button
        v-for="entry in kinds"
        :key="entry.value"
        :label="label(entry.value)"
        :icon="entry.icon"
        :aria-pressed="kind === entry.value"
        :variant="kind === entry.value ? 'faded' : 'ghost'"
        color="slate"
        size="sm"
        :disabled="saving"
        @click="kind = entry.value"
      />
    </div>
    <div
      class="grid min-w-0"
      :class="contained ? '' : 'lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]'"
    >
      <p class="col-span-full m-0 px-4 pt-4 text-xs text-n-slate-11">
        {{
          kind === 'tags'
            ? t('MEDIA_LIBRARY.DELETE_TAG_HELP')
            : kind === 'categories'
              ? t('MEDIA_LIBRARY.CATEGORY_HELP')
              : kind === 'product_classes'
                ? t('MEDIA_LIBRARY.CLASS_HELP')
                : t('MEDIA_LIBRARY.TAXONOMY.HELP')
        }}
      </p>
      <form
        v-if="kind !== 'tags'"
        class="flex min-w-0 flex-col gap-4 p-4"
        @submit.prevent="save"
      >
        <h3 class="m-0 text-sm font-semibold">
          {{
            draft.id
              ? t('MEDIA_LIBRARY.TAXONOMY.EDIT', { type: label(kind) })
              : t('MEDIA_LIBRARY.TAXONOMY.NEW', { type: label(kind) })
          }}
        </h3>
        <label
          for="taxonomy-name"
          class="flex flex-col gap-1.5 text-xs text-n-slate-11"
        >
          {{ t('MEDIA_LIBRARY.TAXONOMY.NAME') }}
          <input
            id="taxonomy-name"
            v-model="draft.name"
            type="text"
            required
            maxlength="200"
            class="!mb-0 !h-10 !w-full !rounded-lg !border-n-weak !bg-n-field !px-3 !py-2 !text-sm !text-n-slate-12"
            :disabled="saving"
          />
        </label>
        <template v-if="kind === 'products'">
          <div class="flex flex-col gap-1.5">
            <span class="text-xs text-n-slate-11">{{
              t('MEDIA_LIBRARY.CATEGORY')
            }}</span>
            <ComboBox
              :model-value="draft.category_id || ''"
              :options="options(taxonomy.categories)"
              :allow-deselect="false"
              :aria-label="t('MEDIA_LIBRARY.CATEGORY')"
              :disabled="saving"
              @update:model-value="draft.category_id = $event"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-xs text-n-slate-11">{{
              t('MEDIA_LIBRARY.PRODUCT_CLASS')
            }}</span>
            <ComboBox
              :model-value="draft.product_class_id || ''"
              :options="options(taxonomy.product_classes)"
              :allow-deselect="false"
              :aria-label="t('MEDIA_LIBRARY.PRODUCT_CLASS')"
              :disabled="saving"
              @update:model-value="draft.product_class_id = $event"
            />
          </div>
        </template>
        <label
          v-if="kind === 'product_classes'"
          for="class-checklist"
          class="flex flex-col gap-1.5 text-xs text-n-slate-11"
        >
          {{ t('MEDIA_LIBRARY.TAXONOMY.REQUIREMENTS') }}
          <textarea
            id="class-checklist"
            v-model="requirements"
            rows="4"
            maxlength="4000"
            class="!mb-0 min-h-28 !w-full !rounded-lg !border-n-weak !bg-n-field !p-3 !text-sm !text-n-slate-12"
            :disabled="saving"
            :placeholder="t('MEDIA_LIBRARY.TAXONOMY.EXAMPLES')"
            aria-describedby="checklist-help"
          />
          <span id="checklist-help" class="text-xs text-n-slate-11">
            {{ t('MEDIA_LIBRARY.TAXONOMY.CHECKLIST_HELP') }}
          </span>
        </label>
        <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
          {{ error }}
        </p>
        <div
          class="flex items-center justify-end gap-2 border-t border-n-weak pt-4"
        >
          <Button
            v-if="draft.id"
            variant="outline"
            color="slate"
            :label="t('MEDIA_LIBRARY.CANCEL')"
            :disabled="saving"
            @click="edit()"
          />
          <Button
            type="submit"
            icon="i-lucide-save"
            color="blue"
            :label="t('MEDIA_LIBRARY.SAVE')"
            :is-loading="saving"
            :disabled="saving || !draft.name.trim()"
          />
        </div>
      </form>
      <div
        class="min-w-0 border-t border-n-weak p-4"
        :class="contained ? '' : 'lg:border-l lg:border-t-0'"
      >
        <h3 class="m-0 flex items-center gap-2 text-sm font-semibold">
          {{ label(kind) }}
          <span class="text-xs font-normal tabular-nums text-n-slate-11">{{
            records.length
          }}</span>
        </h3>
        <div
          v-if="!records.length"
          class="flex flex-col items-center gap-3 px-4 py-10 text-center text-n-slate-11"
        >
          <span class="i-lucide-folder-open size-6" aria-hidden="true" />
          <p class="m-0 max-w-xs text-sm">
            {{ t('MEDIA_LIBRARY.TAXONOMY.EMPTY') }}
          </p>
        </div>
        <ul
          v-else
          class="m-0 mt-3 max-h-80 list-none divide-y divide-n-weak overflow-y-auto p-0"
        >
          <li
            v-for="record in records"
            :key="`${kind}-${record.id}`"
            class="flex flex-wrap items-center justify-between gap-3 rounded-lg px-2 py-3"
            :class="draft.id === record.id ? 'bg-n-alpha-1' : ''"
          >
            <div class="w-full min-w-0">
              <button
                v-if="kind === 'products'"
                type="button"
                class="text-left text-sm font-medium text-n-slate-12 hover:text-n-blue-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                :disabled="saving"
                @click="emit('open-product', record)"
              >
                {{ record.name }}
              </button>
              <p v-else class="m-0 break-words text-sm font-medium">
                {{ record.name }}
              </p>
              <p
                v-if="record.photo_requirements?.length"
                class="mb-0 mt-1 break-words text-xs text-n-slate-11"
              >
                {{ record.photo_requirements.join(' · ') }}
              </p>
              <p
                v-if="record.checklist?.required.length"
                class="mb-0 mt-1 text-xs text-n-slate-11"
              >
                {{
                  t('MEDIA_LIBRARY.COVERAGE', {
                    covered: record.checklist.covered.length,
                    total: record.checklist.required.length,
                  })
                }}
              </p>
            </div>
            <MediaLibraryDelete
              class="min-w-0 flex-1"
              :name="record.name"
              :help="
                t(
                  kind === 'tags'
                    ? 'MEDIA_LIBRARY.DELETE_TAG_HELP'
                    : kind === 'products'
                      ? 'MEDIA_LIBRARY.DELETE_PRODUCT_HELP'
                      : 'MEDIA_LIBRARY.DELETE_TAXONOMY_HELP'
                )
              "
              :action="() => MediaLibraryAPI.deleteTaxonomy(kind, record)"
              :disabled="saving"
              @busy="
                saving = $event;
                emit('busy', $event);
              "
              @deleted="emit('deleted')"
            />
            <Button
              v-if="kind !== 'tags'"
              icon="i-lucide-pencil"
              variant="outline"
              color="slate"
              size="sm"
              :aria-label="
                t('MEDIA_LIBRARY.TAXONOMY.EDIT_RECORD', { name: record.name })
              "
              :title="
                t('MEDIA_LIBRARY.TAXONOMY.EDIT_RECORD', { name: record.name })
              "
              :disabled="saving"
              @click="
                kind === 'products'
                  ? emit('open-product', record, 'details')
                  : edit(record)
              "
            />
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
